'use client'
import { type FC, useEffect, useMemo, useState, useRef } from 'react'
import { useParams } from 'next/navigation'
import Link from 'next/link'
import { motion, Variants } from 'motion/react'
import styled from 'styled-components'
import { LANGUAGES, type TypeLang } from '@/schema'
import { LANG } from '@/utils'
import { rgbAlpha } from '@/styles'


const LANG_TEXT = {
  cat: 'Català',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português'
}


const getItems = (data, lang:TypeLang) => data.map((itemLang:TypeLang, idx) => {
  return (
    <li key={idx}>
      <Link href={`/${itemLang}`} rel='alternate' hrefLang={itemLang} {...lang === itemLang && { className: 'ac' }}>
        {LANG_TEXT?.[itemLang]}
      </Link>
    </li>
  )
})

export const LangDropdown:FC = () => {
  const { lang } = useParams() as { lang: TypeLang}
  const Items = useMemo(() => getItems(LANGUAGES, lang), [lang])
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleTriggerClick = () => setIsOpen((prev) => !prev)

  const handleEsc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') setIsOpen(false)
  }

  const handleClickOutside = (e: MouseEvent): void => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleEsc)
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      window.removeEventListener('keydown', handleEsc)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <Div ref={dropdownRef} className='dropdown--lang'>
      <button id='lang-toggle' aria-haspopup='true' aria-expanded={isOpen} onClick={handleTriggerClick}>
        <span id='current-lang'>{lang || LANG}</span>
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path d='M0.5 4.25L8 11.75L15.5 4.25' stroke='var(--color-navbar-link)' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </button>
      <Ul id='lang-menu' {...!isOpen && { hidden: true }} variants={UlVariants} initial={false} animate={isOpen ? 'expand' : 'collapse'}>
        {Items}
      </Ul>
    </Div>
  )
}

const Div = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  position: relative;

  button {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: 2px;
    height: 30px;
    justify-content: center;
    padding: 2px;
    width: fit-content;

    span {
      display: inline-block;
      text-transform: uppercase;
      text-align: center;
      width: 25px;
    }

    svg,
    img {
      height: 10px;
      transition: transform 150ms ease;
      object-fit: contain;
      width: 10px;
    }

    svg {
      path {
        transition: stroke 150ms ease;
      }
    }

    &[aria-expanded='true'] {
      svg,
      img {
        transform: rotate(180deg);
      }
    }
  }
`

const Ul = styled(motion.ul)`
  align-items: center;
  background-color: var(--color-background);
  border: 1px solid rgba(255, 255, 255, .1);
  border-radius: 2px;
  /* box-shadow: 2px 2px 3px 0 rgba(0,0,0, .1); */
  bottom: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
  justify-content: flex-end;
  position: absolute;
  right: 0;
  top: 30px;
  width: fit-content;

  li {
    min-width: 75px;
  }

  a {
    color: var(--color-text);
    display: inline-block;
    min-width: 35px;
    padding: 10px;
    text-align: center;
    transition: background-color 250ms ease, color 250ms ease;
    width: 100%;

    &.ac {
      color: var(--color-gold);
      font-weight: 500;
    }

    &:hover {
      background-color: ${rgbAlpha('--color-white', .05)};
    }
  }
`

const UlVariants:Variants = {
  expand: {
    y: 0,
    opacity: 1,
    transition: {
      duration: .2
    }
  },
  collapse: {
    y: 5,
    opacity: 0,
    transition: {
      duration: .2
    }
  }
}
